import QueryString from 'query-string';

/*
 * Route
 *
 * The URL is the source of truth for which view is showing. App subscribes to
 * this module and derives its state from it, so a nav click and a browser back
 * button travel exactly the same path — there is no second code path that can
 * drift.
 *
 * Why query parameters rather than real paths. A replicated website is served
 * by index.php behind rewrite rules that live outside this repository and
 * currently map only `/{web_alias}` (and custom domains at their root). A path
 * like /acme-safety/products/water-sensor would 404 until those rules change,
 * and they are not ours to change. Writing to the query string instead keeps
 * the path untouched, so the rewrite keeps resolving the dealership exactly as
 * it always has, and deep links, refreshes, and sharing all work today.
 *
 * `route` stays the single canonical parameter because index.php already
 * switches on it to build the Open Graph tags. Anything else a view needs
 * rides alongside it — `handle` for a product, `token` and `id` for the survey
 * views that already read them.
 *
 * Moving to real paths later is this file plus a rewrite rule; nothing that
 * imports it needs to change.
 */

const listeners = [];

const notify = () => {
    let current = Route.get();
    listeners.forEach(listener => {
        try {
            listener(current);
        } catch(e) {
            console.error(`Route listener failed: ${e.message}`);
        }
    });
}

const Route = {

    // { route: 'products', params: { handle: 'water-sensor' } }
    get: () => {
        let { route, ...params } = QueryString.parse(window.location.search);
        return {
            params: params,
            route: route || null
        };
    },

    // A real URL, so navigation can be rendered as an anchor where that is the
    // honest markup — middle click and "open in new tab" then behave.
    href: (route, params) => {
        let query = QueryString.stringify({ route: route || undefined, ...params });
        return `${window.location.pathname}${query ? `?${query}` : ''}`;
    },

    push: (route, params) => {
        let next = Route.href(route, params);
        // Guard against stacking an identical entry, which would otherwise
        // make the back button appear to do nothing.
        if(next !== `${window.location.pathname}${window.location.search}`) {
            window.history.pushState({ route: route || null }, '', next);
        }
        notify();
    },

    replace: (route, params) => {
        window.history.replaceState({ route: route || null }, '', Route.href(route, params));
        notify();
    },

    subscribe: listener => {
        listeners.push(listener);
        return () => {
            let index = listeners.indexOf(listener);
            if(index >= 0) {
                listeners.splice(index, 1);
            }
        };
    }
}

// The back and forward buttons move the URL without going through push, so
// the same notification has to be raised from the browser's own event.
window.addEventListener('popstate', notify);

export default Route;
